import { useState } from 'react';
import { SESSIONS as sessions } from '../../data/sessions';
import { C, Badge, statusColor, statusLabel, phaseColor } from './shared';

export default function SessionsTab() {
  const latestDone = [...sessions].reverse().find(s => s.status === 'done');
  const [openIds,         setOpenIds]         = useState(new Set(latestDone ? [latestDone.id] : []));
  const [collapsedPhases, setCollapsedPhases] = useState(new Set());
  const [expandedDone,    setExpandedDone]    = useState(new Set()); // done collapsed by default

  // Phase B+ normalised into Phase B — no separate B+ phase
  const phaseOrderMap = {
    'Phase 0 — Foundation':           0,
    'Phase 1 — Platform Core':        1,
    'Phase 2 — Signal Collection':    2,
    'Phase 3 — OCI Infrastructure':   3,
    'Phase 4 — Pipeline Data Layer':  4,
    'Phase 5 — Screener Completion':  5,
    'Phase 6 — Operational Hardening': 6,
    'Phase 7 — Revenue Gate':         7,
    'Phase 8 — D3 Foundation':        8,
    'Phase 9 — D3 Shadow':            9,
    'Phase 10 — D3 Paper':            10,
    'Phase 11 — D3 Capital Deploy':   11,
    'Phase 12 — Products 3 and 4':    12,
    'Every Phase':                    13,
    'Monetise':                       14,
  };

  const normalizePhase = (ph) => ph || 'Phase 4 — Pipeline Data Layer';

  const statusOrder = { next: 0, done: 1, planned: 2, future: 3 };
  const sorted = [...sessions].sort((a, b) => {
    const pa = phaseOrderMap[a.phase] ?? 99, pb = phaseOrderMap[b.phase] ?? 99;
    if (pa !== pb) return pa - pb;
    const sa = statusOrder[a.status] ?? 2, sb = statusOrder[b.status] ?? 2;
    if (sa !== sb) return sa - sb;
    if (a.status === 'done') return sessions.indexOf(b) - sessions.indexOf(a);
    return sessions.indexOf(a) - sessions.indexOf(b);
  });

  // Group by normalised phase
  const groups = {};
  sorted.forEach(s => {
    const ph = normalizePhase(s.phase);
    if (!groups[ph]) groups[ph] = [];
    groups[ph].push(s);
  });

  const phaseDisplayOrder = [
    'Phase 0 — Foundation',
    'Phase 1 — Platform Core',
    'Phase 2 — Signal Collection',
    'Phase 3 — OCI Infrastructure',
    'Phase 4 — Pipeline Data Layer',
    'Phase 5 — Screener Completion',
    'Phase 6 — Operational Hardening',
    'Phase 7 — Revenue Gate',
    'Phase 8 — D3 Foundation',
    'Phase 9 — D3 Shadow',
    'Phase 10 — D3 Paper',
    'Phase 11 — D3 Capital Deploy',
    'Phase 12 — Products 3 and 4',
    'Every Phase',
    'Monetise',
  ];
  const phaseLabels = {
    'Phase 0 — Foundation':           '🏗  Phase 0 — Foundation',
    'Phase 1 — Platform Core':        '🔵  Phase 1 — Platform Core',
    'Phase 2 — Signal Collection':    '🔵  Phase 2 — Signal Collection',
    'Phase 3 — OCI Infrastructure':   '🟢  Phase 3 — OCI Infrastructure',
    'Phase 4 — Pipeline Data Layer':  '🟡  Phase 4 — Pipeline Data Layer  ⬅ PILLAR FOR ALL 4 PRODUCTS',
    'Phase 5 — Screener Completion':  '🟢  Phase 5 — Screener Completion',
    'Phase 6 — Operational Hardening':'🟠  Phase 6 — Operational Hardening',
    'Phase 7 — Revenue Gate':         '🟠  Phase 7 — Revenue Gate',
    'Phase 8 — D3 Foundation':        '🔴  Phase 8 — D3 Foundation',
    'Phase 9 — D3 Shadow':            '🔴  Phase 9 — D3 Shadow',
    'Phase 10 — D3 Paper':            '🔴  Phase 10 — D3 Paper',
    'Phase 11 — D3 Capital Deploy':   '🔴  Phase 11 — D3 Capital Deploy',
    'Phase 12 — Products 3 and 4':    '🟣  Phase 12 — Products 3 and 4',
    'Every Phase':                    '⚙️  Standing Rules (Every Phase)',
    'Monetise':                       '💰  Monetise',
  };

  const allPhases    = phaseDisplayOrder.filter(ph => groups[ph]?.length > 0);
  const doneTotal    = sessions.filter(s => s.status === 'done').length;
  const totalCount   = sessions.length;

  const toggle      = (id) => setOpenIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const togglePhase = (ph) => setCollapsedPhases(prev => { const n = new Set(prev); n.has(ph) ? n.delete(ph) : n.add(ph); return n; });
  const toggleDone  = (ph) => setExpandedDone(prev => { const n = new Set(prev); n.has(ph) ? n.delete(ph) : n.add(ph); return n; });

  const expandAll  = () => { setOpenIds(new Set(sessions.map(s => s.id))); setCollapsedPhases(new Set()); setExpandedDone(new Set(allPhases)); };
  const collapseAll = () => { setOpenIds(new Set()); setExpandedDone(new Set()); setCollapsedPhases(new Set()); };

  const renderSession = (s) => {
    const isOpen = openIds.has(s.id);
    const ac = statusColor[s.status];
    const pc = phaseColor[normalizePhase(s.phase)] || C.muted;
    return (
      <div key={s.id} style={{ background: C.card, border: '1px solid ' + (isOpen ? ac : C.border),
        borderLeft: '3px solid ' + pc, borderRadius: 10, overflow: 'hidden', marginBottom: 3 }}>
        <div onClick={() => toggle(s.id)} style={{ padding: '11px 18px', display: 'flex',
          alignItems: 'center', gap: 10, cursor: 'pointer', userSelect: 'none' }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: ac, flexShrink: 0 }} />
          <div style={{ background: C.surface, borderRadius: 4, padding: '2px 8px', fontSize: 11,
            color: C.muted, fontFamily: 'monospace', minWidth: 60, textAlign: 'center', flexShrink: 0 }}>
            {(s.status === 'done' || s.status === 'next') ? (s.sessionNumber || s.id) : 'TBD'}
          </div>
          <span style={{ flex: 1, color: C.text, fontWeight: 700, fontSize: 13,
            minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {s.name}
          </span>
          {s.date && <span style={{ fontSize: 11, color: C.muted, flexShrink: 0 }}>{s.date}</span>}
          {s.commit && <span style={{ fontSize: 10, color: C.muted, fontFamily: 'monospace',
            background: C.surface, padding: '2px 6px', borderRadius: 4, flexShrink: 0 }}>{s.commit}</span>}
          <Badge color={pc}>{normalizePhase(s.phase)}</Badge>
          <Badge color={ac}>{statusLabel[s.status]}</Badge>
          <span style={{ color: C.muted, fontSize: 10, flexShrink: 0 }}>{isOpen ? '▼' : '▶'}</span>
        </div>
        {isOpen && (
          <div style={{ padding: '4px 18px 14px 18px', borderTop: '1px solid ' + C.border }}>
            <div style={{ paddingTop: 8, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 14px' }}>
              {s.items.map((item, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                  <span style={{ color: ac, fontSize: 10, flexShrink: 0, marginTop: 3 }}>▸</span>
                  <span style={{ color: C.subtle, fontSize: 12, lineHeight: 1.5 }}>{item}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ display: 'grid', gap: 6 }}>

      {/* ── Top bar ── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        flexWrap: 'wrap', gap: 8, padding: '10px 16px',
        background: C.card, borderRadius: 10, border: '1px solid ' + C.border, marginBottom: 4 }}>
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center' }}>
          {Object.entries(statusLabel).map(([k, v]) => (
            <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <div style={{ width: 7, height: 7, borderRadius: '50%', background: statusColor[k] }} />
              <span style={{ color: statusColor[k], fontSize: 11, fontWeight: 700 }}>{v}</span>
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 11, color: C.muted }}>
            <span style={{ color: C.green, fontWeight: 700 }}>{doneTotal}</span>
            <span> / {totalCount} complete</span>
          </span>
          {[['Expand All', expandAll], ['Collapse All', collapseAll]].map(([label, fn]) => (
            <button key={label} onClick={fn} style={{ padding: '3px 10px', background: 'transparent',
              border: '1px solid ' + C.border, borderRadius: 6,
              color: C.muted, fontSize: 11, cursor: 'pointer', fontFamily: 'inherit' }}>{label}</button>
          ))}
        </div>
      </div>

      {/* ── Phase groups ── */}
      {allPhases.map(ph => {
        const phaseSess  = groups[ph];
        const doneSess   = phaseSess.filter(s => s.status === 'done');
        const activeSess = phaseSess.filter(s => s.status !== 'done');
        const isCollapsed  = collapsedPhases.has(ph);
        const isDoneOpen   = expandedDone.has(ph);
        const pc           = phaseColor[ph] || C.muted;
        const nextCount    = activeSess.filter(s => s.status === 'next').length;
        const plannedCount = activeSess.filter(s => s.status === 'planned').length;
        const futureCount  = activeSess.filter(s => s.status === 'future').length;

        const countParts = [
          doneSess.length   > 0 ? `${doneSess.length} done`    : '',
          nextCount         > 0 ? `${nextCount} next`           : '',
          plannedCount      > 0 ? `${plannedCount} planned`     : '',
          futureCount       > 0 ? `${futureCount} future`       : '',
        ].filter(Boolean).join(' · ');

        return (
          <div key={ph}>

            {/* Phase header — clickable to collapse/expand */}
            <div onClick={() => togglePhase(ph)} style={{ padding: '9px 14px', cursor: 'pointer',
              userSelect: 'none', background: 'rgba(255,255,255,0.025)',
              border: '1px solid rgba(255,255,255,0.07)', borderLeft: '3px solid ' + pc,
              borderRadius: 8, marginTop: 10, marginBottom: 4,
              display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ color: pc, fontSize: 10, flexShrink: 0 }}>{isCollapsed ? '▶' : '▼'}</span>
              <span style={{ fontSize: 11, fontWeight: 800, color: pc,
                letterSpacing: '0.06em', textTransform: 'uppercase', flex: 1 }}>
                {phaseLabels[ph] || ph}
              </span>
              <span style={{ fontSize: 10, color: C.muted, flexShrink: 0 }}>{countParts}</span>
            </div>

            {!isCollapsed && (
              <div>

                {/* Done sub-section — collapsed by default */}
                {doneSess.length > 0 && (
                  <div style={{ marginBottom: 4 }}>
                    <div onClick={() => toggleDone(ph)} style={{ padding: '6px 14px',
                      cursor: 'pointer', userSelect: 'none',
                      display: 'flex', alignItems: 'center', gap: 8,
                      borderLeft: '3px solid ' + C.border,
                      background: C.surface + '55', borderRadius: '0 6px 6px 0' }}>
                      <span style={{ color: C.muted, fontSize: 10 }}>{isDoneOpen ? '▼' : '▶'}</span>
                      <span style={{ color: C.green, fontSize: 11, fontWeight: 700 }}>DONE</span>
                      <span style={{ color: C.muted, fontSize: 10 }}>
                        {doneSess.length} session{doneSess.length !== 1 ? 's' : ''}
                        {!isDoneOpen ? ' — click to expand' : ''}
                      </span>
                    </div>
                    {isDoneOpen && <div style={{ marginTop: 3 }}>{doneSess.map(renderSession)}</div>}
                  </div>
                )}

                {/* Active + planned + future — always visible when phase open */}
                {activeSess.length > 0 && <div>{activeSess.map(renderSession)}</div>}

              </div>
            )}

          </div>
        );
      })}

    </div>
  );
}
